import React, { useState, useEffect, lazy, Suspense, useCallback } from "react";
import MetaTags from 'react-meta-tags';
import { Container, Row, Col } from "reactstrap";
import { Link, useHistory, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  RiFileChartLine,
  RiCalendarEventLine,
  RiUserHeartLine,
  RiMessage3Line,
  RiMedicineBottleLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiTimeLine,
  RiUserVoiceLine,
  RiWhatsappLine,
  RiMailLine,
  RiCake2Line,
  RiFolderShield2Line,
  RiStethoscopeLine,
  RiSearchLine,
  RiMoneyDollarCircleLine,
  RiDashboardLine,
  RiPieChartLine,
  RiSecurePaymentLine,
  RiWallet3Line,
  RiNotificationLine
} from "react-icons/ri";
import "./reports.scss";

const reportCategories = [
  {
    id: "appointments",
    title: "Appointments & Schedules",
    icon: RiCalendarEventLine,
    color: "#F8CA15"
  },
  {
    id: "patients",
    title: "Patients & Clinical Master",
    icon: RiUserHeartLine,
    color: "#16a34a"
  },
  {
    id: "financial",
    title: "Financial & Invoices",
    icon: RiMoneyDollarCircleLine,
    color: "#e11d48"
  },
  {
    id: "communications",
    title: "Communications & Reminders",
    icon: RiMessage3Line,
    color: "#7c3aed"
  },
  {
    id: "labwork",
    title: "Dental Lab & Prosthetics",
    icon: RiMedicineBottleLine,
    color: "#d97706"
  },
];

const reportModules = {
  "appointments": [
    {
      module: "AppointmentsReport",
      folder: "appointments",
      title: "Appointments Schedule Report",
      desc: "Comprehensive operatory booking registry, chair utilization, slot attendance, and completed treatments.",
      icon: RiCalendarEventLine,
      color: "#F8CA15",
      id: 1,
      isActive: 1
    },
    {
      module: "WaitingReport",
      folder: "appointments",
      title: "Waiting Queue & Transit Report",
      desc: "In-clinic waiting lounge duration, chair transition timeline, and patient wait time metrics.",
      icon: RiTimeLine,
      color: "#059669",
      id: 2,
      isActive: 1
    },
    {
      module: "RemindersReport",
      folder: "appointments",
      title: "Appointment Reminders Register",
      desc: "Upcoming automated reminder queues, visit confirmations, and notification logs.",
      icon: RiNotificationLine,
      color: "#7c3aed",
      id: 3,
      isActive: 1
    }
  ],
  "patients": [
    {
      module: "PatientReport",
      folder: "patients",
      title: "Patient Demographics & Registry",
      desc: "Registered patient demographic directory, age distribution, gender analytics, and clinical case histories.",
      icon: RiUserHeartLine,
      color: "#16a34a",
      id: 4,
      isActive: 1
    },
    {
      module: "DoctorReport",
      folder: "patients",
      title: "Doctor & Specialist Roster",
      desc: "Doctor specialization directories, clinic registrations, and operatory chair allocations.",
      icon: RiStethoscopeLine,
      color: "#F8CA15",
      id: 5,
      isActive: 1
    },
    {
      module: "DoctorWisePatient",
      folder: "patients",
      title: "Doctor-Wise Patient Distribution",
      desc: "Patient distribution by attending specialist, scheduled consultations, and completed procedures.",
      icon: RiUserVoiceLine,
      color: "#7c3aed",
      id: 6,
      isActive: 1
    },
    {
      module: "ReferenceReport",
      folder: "patients",
      title: "Referrals & Acquisition Channels",
      desc: "Detailed source analytics for walk-ins, doctor referrals, online marketing, and insurance panels.",
      icon: RiFileChartLine,
      color: "#d97706",
      id: 7,
      isActive: 1
    },
    {
      module: "PatientDocumentReport",
      folder: "patients",
      title: "Patient Documents & Scan Archive",
      desc: "Digital X-Rays, CBCT scans, prescriptions, clinical consent forms, and document records.",
      icon: RiFolderShield2Line,
      color: "#e11d48",
      id: 8,
      isActive: 1
    },
    {
      module: "ClinicDashboard",
      folder: "patients",
      title: "Clinical OPD Summary Dashboard",
      desc: "Aggregated OPD executive summary, treatment statistics, and patient inflow volume.",
      icon: RiDashboardLine,
      color: "#F8CA15",
      id: 9,
      isActive: 1
    },
    {
      module: "ClinicDataAnalysis",
      folder: "patients",
      title: "Clinical Analytics & Metrics",
      desc: "Procedure trends, tooth examination frequencies, and patient retention analytics.",
      icon: RiPieChartLine,
      color: "#059669",
      id: 10,
      isActive: 1
    },
    {
      module: "GeneralReport",
      folder: "patients",
      title: "General Practice Register",
      desc: "General clinical audit logs, patient registrations, and OPD visit histories.",
      icon: RiFileChartLine,
      color: "#64748b",
      id: 11,
      isActive: 1
    }
  ],
  "financial": [
    {
      module: "CollectionReport",
      folder: "patients",
      title: "Collections & Cashflow Register",
      desc: "Cash, UPI, Card, and Bank settlement logs with real-time cashier collection summaries.",
      icon: RiWallet3Line,
      color: "#16a34a",
      id: 12,
      isActive: 1
    },
    {
      module: "BillingReport",
      folder: "patients",
      title: "Invoices & Billing Summary",
      desc: "Official itemized dental bills, treatment invoicing breakdowns, and tax receipts.",
      icon: RiFileChartLine,
      color: "#F8CA15",
      id: 13,
      isActive: 1
    },
    {
      module: "OutstandingReport",
      folder: "patients",
      title: "Outstanding Dues & Balances",
      desc: "Pending patient treatment balances, payment aging reports, and credit ledger records.",
      icon: RiMoneyDollarCircleLine,
      color: "#e11d48",
      id: 14,
      isActive: 1
    },
    {
      module: "RevenueReport",
      folder: "patients",
      title: "Procedure Yield & Revenue",
      desc: "Treatment-wise gross revenue, dental procedure profitability, and financial yields.",
      icon: RiPieChartLine,
      color: "#7c3aed",
      id: 15,
      isActive: 1
    },
    {
      module: "OnlinePatientPaymentReport",
      folder: "patients",
      title: "Online Payment Gateway Transactions",
      desc: "Patient portal digital payments, QR payments, and transaction reconciliation.",
      icon: RiSecurePaymentLine,
      color: "#059669",
      id: 16,
      isActive: 1
    }
  ],
  "communications": [
    {
      module: "WhatsappSMSReport",
      folder: "communications",
      title: "WhatsApp & SMS Dispatch Logs",
      desc: "Automated WhatsApp appointment confirmations, reminder messages, and delivery audit logs.",
      icon: RiWhatsappLine,
      color: "#25d366",
      id: 17,
      isActive: 1
    },
    {
      module: "EmailReport",
      folder: "communications",
      title: "Email Dispatch & Receipts",
      desc: "Sent digital invoices, treatment estimates, electronic prescriptions, and confirmation emails.",
      icon: RiMailLine,
      color: "#F8CA15",
      id: 18,
      isActive: 1
    },
    {
      module: "BirthdayReport",
      folder: "communications",
      title: "Birthday & Celebration Automations",
      desc: "Upcoming patient birthdays, automated greetings dispatch, and loyalty discount triggers.",
      icon: RiCake2Line,
      color: "#ec4899",
      id: 19,
      isActive: 1
    },
    {
      module: "PatientFollowupReport",
      folder: "communications",
      title: "Post-Op Care & Follow-up Logs",
      desc: "Post-procedure check-in registry, healing progress notes, and patient feedback surveys.",
      icon: RiTimeLine,
      color: "#059669",
      id: 20,
      isActive: 1
    }
  ],
  "labwork": [
    {
      module: "LabworkReport",
      folder: "labwork",
      title: "Dental Lab & Prosthetics Tracker",
      desc: "Crown & bridge fabrication orders, aligner batches, delivery timelines, and technician logs.",
      icon: RiMedicineBottleLine,
      color: "#d97706",
      id: 21,
      isActive: 1
    }
  ]
};

export const toKebabCase = (str) => {
  if (!str) return "";
  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
};

const findReportModule = (categoryKey, reportParam) => {
  if (!reportParam) return null;
  const list = reportModules[categoryKey] || [];
  const lowerParam = reportParam.toLowerCase();
  return list.find(
    (c) =>
      c.module.toLowerCase() === lowerParam ||
      toKebabCase(c.module) === lowerParam
  );
};

// Component cache to avoid re-creating lazy elements
const lazyComponentCache = {};

const getLazyComponent = (folder, moduleName) => {
  const key = `${folder}/${moduleName}`;
  if (!lazyComponentCache[key]) {
    lazyComponentCache[key] = lazy(() => import(`./${folder}/${moduleName}`));
  }
  return lazyComponentCache[key];
};

const Reports = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams();
  const location = useLocation();

  // Extract from path params or query params
  const searchParams = new URLSearchParams(location.search);
  const routeCategory = (params.category || searchParams.get('category') || "appointments").toLowerCase();
  const routeReportParam = params.report || searchParams.get('report') || null;
  const initialMatch = findReportModule(routeCategory, routeReportParam);

  const [selectedCategory, setSelectedCategory] = useState(routeCategory);
  const [selectedReportModule, setSelectedReportModule] = useState(initialMatch ? initialMatch.module : null);
  const [searchTerm, setSearchTerm] = useState("");

  // Sync state when URL params change
  useEffect(() => {
    const cat = (params.category || searchParams.get('category') || "appointments").toLowerCase();
    if (cat && reportModules[cat]) {
      setSelectedCategory(cat);
    }
    const rep = params.report || searchParams.get('report');
    if (rep) {
      const match = findReportModule(cat, rep);
      if (match) {
        setSelectedReportModule(match.module);
      }
    } else if (!rep) {
      setSelectedReportModule(null);
    }
  }, [params.category, params.report, location.search]);

  // Handle Tab Switch
  const handleCategorySelect = (categoryId) => {
    const catSlug = categoryId.toLowerCase();
    setSelectedCategory(catSlug);
    setSelectedReportModule(null);
    setSearchTerm("");
    history.push(`/reports/${catSlug}`);
  };

  // Handle Launching a Report
  const handleLaunchReport = (card, categoryKey) => {
    const catSlug = categoryKey.toLowerCase();
    setSelectedCategory(catSlug);
    setSelectedReportModule(card.module);
    history.push(`/reports/${catSlug}/${toKebabCase(card.module)}`);
  };

  // Handle Going Back to Hub
  const handleBackToHub = () => {
    setSelectedReportModule(null);
    history.push(`/reports/${selectedCategory}`);
  };

  const currentCategoryObj = reportCategories.find(c => c.id === selectedCategory) || reportCategories[0];
  const categoryModuleList = reportModules[selectedCategory] || [];
  const activeReport = selectedReportModule ? categoryModuleList.find(c => c.module === selectedReportModule) : null;

  const SelectedComponent = activeReport
    ? getLazyComponent(activeReport.folder || selectedCategory, activeReport.module)
    : null;

  const filteredCards = categoryModuleList
    .filter(v => v.isActive)
    .filter(v => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return v.title.toLowerCase().includes(term) || v.desc.toLowerCase().includes(term);
    });

  return (
    <div className="page-content reports-page-container">
      <MetaTags>
        <title>{t("Reports")} | {process.env.REACT_APP_TITLE || 'Dental OPD'}</title>
      </MetaTags>
      <Container fluid>
        {/* Top Header Banner */}
        <div className="reports-top-header d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="reports-brand-badge">
              <RiFileChartLine />
            </div>
            <div>
              <h4 className="reports-header-title">{t("Reports & Analytics Hub")}</h4>
              <p className="reports-header-sub">
                Comprehensive clinical registries, financial collections, patient records &amp; communication audits
              </p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Link
              to="/patients"
              className="btn btn-outline-primary d-inline-flex align-items-center gap-1 font-weight-semibold shadow-sm"
            >
              <RiUserHeartLine /> {t("Patients")}
            </Link>
            <Link
              to="/chairs"
              className="btn btn-primary d-inline-flex align-items-center gap-1 font-weight-semibold shadow-sm"
            >
              <RiCalendarEventLine /> {t("Operatory Schedule")}
            </Link>
          </div>
        </div>

        {selectedReportModule && activeReport ? (
          /* Report Viewer Mode */
          <div>
            {/* Breadcrumb Navigation Bar */}
            <div className="d-flex align-items-center justify-content-between p-3 mb-3 bg-white rounded-3 border shadow-sm flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-light border d-inline-flex align-items-center gap-1 font-weight-bold"
                  onClick={handleBackToHub}
                >
                  <RiArrowLeftLine /> Back to Reports Hub
                </button>
                <span className="text-muted">/</span>
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 text-muted font-size-12 fw-semibold"
                  onClick={handleBackToHub}
                >
                  {currentCategoryObj.title}
                </button>
                <span className="text-muted">/</span>
                <strong className="text-primary font-size-14">{activeReport.title}</strong>
              </div>
            </div>

            <Row className="g-3">
              {/* Sidebar report switcher */}
              <Col lg={3}>
                <div className="report-viewer-sidebar">
                  <div className="d-flex align-items-center gap-2 mb-3 px-2">
                    <currentCategoryObj.icon className="font-size-18" style={{ color: currentCategoryObj.color }} />
                    <h6 className="font-weight-bold text-slate-800 mb-0">
                      {currentCategoryObj.title}
                    </h6>
                  </div>
                  <div>
                    {categoryModuleList.filter(v => v.isActive).map((report) => (
                      <button
                        key={report.id}
                        type="button"
                        className={`report-sidebar-btn ${selectedReportModule === report.module ? 'active' : ''}`}
                        onClick={() => handleLaunchReport(report, selectedCategory)}
                      >
                        <report.icon style={{ color: report.color, fontSize: '1.1rem' }} />
                        <span className="text-truncate">{report.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </Col>

              {/* Main report content */}
              <Col lg={9}>
                <div className="report-viewer-card p-4 bg-white rounded-3 border shadow-sm">
                  <Suspense fallback={
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading Report...</span>
                      </div>
                      <p className="text-muted font-size-13 mt-2">Loading Clinical Report Data...</p>
                    </div>
                  }>
                    {SelectedComponent && <SelectedComponent />}
                  </Suspense>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          /* Hub Landing Mode */
          <div>
            {/* Category Navigation Bar */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div className="reports-category-nav flex-grow-1">
                {reportCategories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = selectedCategory === cat.id;
                  const catCount = (reportModules[cat.id] || []).length;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      className={`reports-category-btn ${isActive ? 'active' : ''}`}
                      onClick={() => handleCategorySelect(cat.id)}
                    >
                      <Icon className="font-size-16" />
                      <span>{t(cat.title) || cat.title}</span>
                      <span className="reports-category-count">{catCount}</span>
                    </button>
                  );
                })}
              </div>

              <div className="position-relative" style={{ minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-control rounded-pill"
                  placeholder={`${t("Search reports")}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '2.2rem' }}
                />
                <RiSearchLine
                  className="position-absolute text-muted"
                  style={{ left: '0.85rem', top: '50%', transform: 'translateY(-50%)' }}
                />
              </div>
            </div>

            {/* Report Cards Grid */}
            <Row className="g-3">
              {filteredCards.map((card) => {
                const Icon = card.icon;
                return (
                  <Col md={6} lg={4} key={card.id}>
                    <div
                      className="report-card-item h-100 d-flex flex-column"
                      onClick={() => handleLaunchReport(card, selectedCategory)}
                    >
                      <div>
                        <div
                          className="report-card-icon-box"
                          style={{ backgroundColor: `${card.color}15`, color: card.color }}
                        >
                          <Icon />
                        </div>
                        <h5 className="report-card-title">{t(card.title) || card.title}</h5>
                        <p className="report-card-desc">{t(card.desc) || card.desc}</p>
                      </div>

                      <div className="report-card-action mt-auto pt-2 border-top">
                        <span>{t("Launch Report")}</span>
                        <RiArrowRightLine />
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}
      </Container>
    </div>
  );
};

export default Reports;